import { useState } from "react";
import {
  Card, Table, Stack, TableRow, TableBody, TableCell, Container, Typography,
  TableContainer, TextField, CircularProgress, Button, Grid, Chip, Paper, Divider,
} from "@mui/material";
import Page from "src/components/Page";
import Scrollbar from "src/components/Scrollbar";
import ListHead from "src/components/common/ListHead";
import { asyncRequest } from "src/services/network";
import { alert } from "src/services/alert";
import Confirm from "src/components/common/ConfirmDialog";

const TABLE_HEAD = [
  { id: "id", label: "场次ID", alignRight: false },
  { id: "name", label: "场次名称", alignRight: false },
  { id: "type", label: "类型", alignRight: false },
  { id: "start_time", label: "开始时间", alignRight: false },
  { id: "end_time", label: "结束时间", alignRight: false },
  { id: "status", label: "状态", alignRight: false },
  { id: "dispatch_status", label: "派单状态", alignRight: false },
  { id: "action", label: "操作", alignRight: false },
];

const TYPE_OPTIONS = [
  { value: 1, label: "金豆" },
  { value: 2, label: "银豆" },
];

const SESSION_STATUS_OPTIONS = [
  { value: 0, label: "预约中" },
  { value: 1, label: "抢购中" },
];

export default function DispatchCenter() {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState("");
  const [checkingStatus, setCheckingStatus] = useState(false);
  const [operating, setOperating] = useState(null); // 正在操作的场次ID

  const fetchSessions = async () => {
    setLoading(true);
    try {
      const res = await asyncRequest({
        path: "/jingxi/game/sessions/list",
        data: { pageNum: 1, pageSize: 50 },
      });
      if (res.code === 1) {
        setSessions(res.data || []);
      } else {
        alert(res.msg || "获取场次列表失败");
      }
    } catch (err) {
      console.error(err);
      alert("获取场次列表失败");
    } finally {
      setLoading(false);
    }
  };

  const handleCheckDispatchStatus = async () => {
    if (!sessionId) {
      alert("请先选择场次");
      return;
    }
    setCheckingStatus(true);
    try {
      const res = await asyncRequest({
        path: "/jingxi/game/dispatch/status",
        data: { sessionId: parseInt(sessionId) },
      });
      if (res.code === 1) {
        const dispatched = res.data?.dispatched;
        setSessions((prev) =>
          prev.map((s) =>
            s.id === parseInt(sessionId)
              ? { ...s, _dispatchStatus: dispatched ? "已派单" : "未派单" }
              : s
          )
        );
        alert(dispatched ? "该场次已派单" : "该场次未派单");
      } else {
        alert(res.msg || "查询失败");
      }
    } catch (err) {
      alert("查询失败");
    } finally {
      setCheckingStatus(false);
    }
  };

  const handleResetDispatch = async (id) => {
    Confirm({
      message: "确认重置该场次的派单状态？重置后可重新派单。",
      async onConfirm() {
        setOperating(id);
        try {
          const res = await asyncRequest({
            path: "/jingxi/game/dispatch/reset",
            data: { sessionId: id },
          });
          if (res.code === 1) {
            alert("重置成功");
            setSessions((prev) =>
              prev.map((s) =>
                s.id === id ? { ...s, _dispatchStatus: "未派单" } : s
              )
            );
          } else {
            alert(res.msg || "重置失败");
          }
        } catch (err) {
          alert("重置失败");
        } finally {
          setOperating(null);
        }
      },
    });
  };

  const handleExecuteDispatch = async (id) => {
    Confirm({
      message: "确认手动派单？该操作将先发单再派单。",
      async onConfirm() {
        setOperating(id);
        try {
          const res = await asyncRequest({
            path: "/jingxi/game/dispatch/execute",
            data: { sessionId: id },
          });
          if (res.code === 1) {
            alert("派单完成");
            setSessions((prev) =>
              prev.map((s) =>
                s.id === id ? { ...s, _dispatchStatus: "已派单" } : s
              )
            );
          } else {
            alert(res.msg || "派单失败");
          }
        } catch (err) {
          alert("派单失败");
        } finally {
          setOperating(null);
        }
      },
    });
  };

  const renderType = (type) => {
    const opt = TYPE_OPTIONS.find((o) => o.value === type);
    return opt ? opt.label : "-";
  };

  const renderSessionStatus = (status) => {
    const opt = SESSION_STATUS_OPTIONS.find((o) => o.value === status);
    return opt ? (
      <Chip label={opt.label} color={status === 1 ? "success" : "warning"} size="small" />
    ) : (
      "-"
    );
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return "-";
    const d = new Date(dateStr);
    return d.toLocaleString("zh-CN", { hour12: false });
  };

  return (
    <Page title="派单中心">
      <Container maxWidth="lg">
        <Card sx={{ p: 3 }}>
          <Typography variant="h4" sx={{ mb: 3 }}>派单中心</Typography>

          {/* 快捷操作区 */}
          <Paper sx={{ p: 3, mb: 3, bgcolor: "background.neutral" }}>
            <Typography variant="h6" sx={{ mb: 2 }}>快捷操作</Typography>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  fullWidth
                  size="small"
                  label="场次ID"
                  type="number"
                  value={sessionId}
                  onChange={(e) => setSessionId(e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={8}>
                <Stack direction="row" spacing={1}>
                  <Button
                    variant="contained"
                    color="info"
                    onClick={handleCheckDispatchStatus}
                    disabled={checkingStatus || !sessionId}
                  >
                    {checkingStatus ? "查询中..." : "查询派单状态"}
                  </Button>
                  <Button
                    variant="outlined"
                    color="warning"
                    onClick={() => handleResetDispatch(parseInt(sessionId))}
                    disabled
                    title="暂时屏蔽"
                  >
                    重置派单
                  </Button>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => handleExecuteDispatch(parseInt(sessionId))}
                    disabled
                    title="暂时屏蔽"
                  >
                    手动派单
                  </Button>
                </Stack>
              </Grid>
            </Grid>
          </Paper>

          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
            <Typography variant="h6">场次列表</Typography>
            <Button variant="outlined" onClick={fetchSessions} disabled={loading}>
              {loading ? "加载中..." : "刷新列表"}
            </Button>
          </Stack>

          {loading ? (
            <Stack alignItems="center" sx={{ py: 5 }}><CircularProgress /></Stack>
          ) : sessions.length === 0 ? (
            <Typography color="text.secondary" sx={{ textAlign: "center", py: 5 }}>
              暂无场次数据，请点击刷新加载
            </Typography>
          ) : (
            <Scrollbar>
              <TableContainer sx={{ minWidth: 900 }}>
                <Table>
                  <ListHead headLabel={TABLE_HEAD} />
                  <TableBody>
                    {sessions.map((row) => (
                      <TableRow key={row.id} hover>
                        <TableCell>{row.id}</TableCell>
                        <TableCell>{row.name || "-"}</TableCell>
                        <TableCell>{renderType(row.type)}</TableCell>
                        <TableCell>{formatTime(row.start_time)}</TableCell>
                        <TableCell>{formatTime(row.end_time)}</TableCell>
                        <TableCell>{renderSessionStatus(row.status)}</TableCell>
                        <TableCell>
                          <Chip
                            label={row._dispatchStatus || "未知"}
                            color={row._dispatchStatus === "已派单" ? "success" : "default"}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <Stack direction="row" spacing={1}>
                            <Button
                              size="small"
                              variant="outlined"
                              color="info"
                              onClick={() => {
                                setSessionId(row.id);
                                handleCheckDispatchStatus();
                              }}
                              disabled={operating === row.id}
                            >
                              查询
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              color="warning"
                              onClick={() => handleResetDispatch(row.id)}
                              disabled
                              title="暂时屏蔽"
                            >
                              重置
                            </Button>
                            <Button
                              size="small"
                              variant="contained"
                              color="primary"
                              onClick={() => handleExecuteDispatch(row.id)}
                              disabled
                              title="暂时屏蔽"
                            >
                              派单
                            </Button>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Scrollbar>
          )}
        </Card>
      </Container>
    </Page>
  );
}
